import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit2, Trash2, ToggleLeft, ToggleRight, TrendingUp, Users, Percent, Eye, Star, Download, Upload, X, Inbox, CheckCircle, XCircle, Clock, Mail, Phone, MapPin, Building2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'eigenwerbung', label: 'ADAG Eigenwerbung', color: 'bg-green-100 text-green-800' },
  { id: 'gastro', label: 'Gastronomie', color: 'bg-orange-100 text-orange-800' },
  { id: 'education', label: 'Bildung', color: 'bg-blue-100 text-blue-800' },
  { id: 'tech', label: 'Technologie', color: 'bg-purple-100 text-purple-800' },
  { id: 'retail', label: 'Retail', color: 'bg-yellow-100 text-yellow-800' },
  { id: 'services', label: 'Dienstleistungen', color: 'bg-gray-100 text-gray-800' },
];

const PRODUCTS = [
  { id: 'flyer', label: 'Flyer' },
];

const DEFAULT_DESIGN = {
  headline: 'Die schnellste Druckerei Zürichs',
  subline: 'Express ab 1 Stunde',
  bullets: ['Abschlussarbeiten drucken & binden', 'Flyer, Plakate, Broschüren', 'Copyshop & Binderei'],
  website: 'adagmedia.ch',
  phone: '044 261 35 54',
  address: 'Universitätsstrasse, 8006 Zürich',
  qr_code_url: 'https://adagmedia.ch',
  coupon_code: '',
  coupon_text: '',
  primary_color: '#00963f',
  secondary_color: '#222529'
};

export default function Sponsoring() {
  const [sponsors, setSponsors] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [editingSponsor, setEditingSponsor] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('sponsors'); // 'sponsors' or 'registrations'
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    logo_url: '',
    categories: [],
    discount_percent: 20,
    target_products: ['flyer'],
    is_active: true,
    is_default: false,
    priority: 10,
    valid_until: '',
    design: { ...DEFAULT_DESIGN }
  });

  useEffect(() => {
    loadSponsors();
    loadStats();
    loadRegistrations();
  }, []);

  const loadRegistrations = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sponsoring/registrations`);
      const data = await res.json();
      setRegistrations(data.registrations || []);
    } catch (err) {
      console.error('Failed to load registrations:', err);
    }
  };

  const loadSponsors = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sponsoring/sponsors`);
      const data = await res.json();
      setSponsors(data.sponsors || []);
    } catch (err) {
      console.error('Failed to load sponsors:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sponsoring/stats`);
      const data = await res.json();
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const approveRegistration = async (registrationId) => {
    if (!window.confirm('Diese Registrierung freischalten und als Sponsor anlegen?')) return;
    try {
      const res = await fetch(`${API_URL}/api/sponsoring/registrations/${registrationId}/approve`, {
        method: 'POST'
      });
      const data = await res.json();
      if (data.success) {
        alert('Sponsor erfolgreich freigeschaltet!');
        loadRegistrations();
        loadSponsors();
        loadStats();
      } else {
        alert('Fehler: ' + (data.detail || 'Unbekannter Fehler'));
      }
    } catch (err) {
      console.error('Failed to approve:', err);
      alert('Fehler beim Freischalten');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800"><Clock size={12} /> Ausstehend</span>;
      case 'approved':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"><CheckCircle size={12} /> Freigegeben</span>;
      case 'rejected':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800"><XCircle size={12} /> Abgelehnt</span>;
      default:
        return <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600">{status}</span>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      alert('Bitte nur PNG, JPG, WEBP oder SVG Dateien hochladen.');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('Datei zu gross. Maximal 2MB erlaubt.');
      return;
    }

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const res = await fetch(`${API_URL}/api/sponsoring/sponsors/upload-logo`, {
        method: 'POST',
        body: formDataUpload
      });

      const data = await res.json();
      if (data.success) {
        // Use full URL for the logo
        const logoUrl = `${API_URL}${data.logo_url}`;
        setFormData(prev => ({ ...prev, logo_url: logoUrl }));
      } else {
        alert('Upload fehlgeschlagen: ' + (data.detail || 'Unbekannter Fehler'));
      }
    } catch (err) {
      console.error('Upload failed:', err);
      alert('Upload fehlgeschlagen');
    } finally {
      setUploading(false);
    }
  };

  const removeLogo = () => {
    setFormData(prev => ({ ...prev, logo_url: '' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const initDefaultSponsor = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sponsoring/sponsors/init-adag-default`, {
        method: 'POST'
      });
      const data = await res.json();
      if (data.success) {
        loadSponsors();
        alert('ADAG Default-Sponsor erstellt!');
      } else {
        alert(data.message);
      }
    } catch (err) {
      console.error('Failed to init default:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingSponsor 
        ? `${API_URL}/api/sponsoring/sponsors/${editingSponsor.id}`
        : `${API_URL}/api/sponsoring/sponsors`;
      
      const res = await fetch(url, {
        method: editingSponsor ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        setShowDialog(false);
        setEditingSponsor(null);
        resetForm();
        loadSponsors();
        loadStats();
      }
    } catch (err) {
      console.error('Failed to save sponsor:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sponsor wirklich löschen?')) return;
    try {
      await fetch(`${API_URL}/api/sponsoring/sponsors/${id}`, { method: 'DELETE' });
      loadSponsors();
      loadStats();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const toggleActive = async (sponsor) => {
    try {
      await fetch(`${API_URL}/api/sponsoring/sponsors/${sponsor.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !sponsor.is_active })
      });
      loadSponsors();
    } catch (err) {
      console.error('Failed to toggle:', err);
    }
  };

  const openEdit = (sponsor) => {
    setEditingSponsor(sponsor);
    setFormData({
      name: sponsor.name,
      company: sponsor.company,
      logo_url: sponsor.logo_url || '',
      categories: sponsor.categories || [],
      discount_percent: sponsor.discount_percent,
      target_products: sponsor.target_products || ['flyer'],
      is_active: sponsor.is_active,
      is_default: sponsor.is_default || false,
      priority: sponsor.priority || 10,
      valid_until: sponsor.valid_until || '',
      design: sponsor.design || { ...DEFAULT_DESIGN }
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      company: '',
      logo_url: '',
      categories: [],
      discount_percent: 20,
      target_products: ['flyer'],
      is_active: true,
      is_default: false,
      priority: 10,
      valid_until: '',
      design: { ...DEFAULT_DESIGN }
    });
  };

  const getCategoryStyle = (catId) => {
    const cat = CATEGORIES.find(c => c.id === catId);
    return cat ? cat.color : 'bg-gray-100 text-gray-800';
  };

  const getCategoryLabel = (catId) => {
    const cat = CATEGORIES.find(c => c.id === catId);
    return cat ? cat.label : catId;
  };

  const updateDesign = (field, value) => {
    setFormData(prev => ({
      ...prev,
      design: { ...prev.design, [field]: value }
    }));
  };

  const updateBullet = (index, value) => {
    const newBullets = [...formData.design.bullets];
    newBullets[index] = value;
    updateDesign('bullets', newBullets);
  };

  // Preview Component - Druckfertige Vorschau mit weissem Hintergrund
  const SponsorPreview = ({ design, logoUrl, size = 'a5' }) => {
    const d = design || DEFAULT_DESIGN;
    const scale = size === 'a6' ? 0.7 : size === 'a4' ? 1.4 : 1;
    
    return (
      <div 
        className="border-2 border-gray-300 rounded-lg overflow-hidden shadow-lg mx-auto"
        style={{ 
          width: `${210 * scale}px`, 
          height: `${297 * scale}px`,
          fontFamily: "'Poppins', sans-serif",
          backgroundColor: '#ffffff'
        }}
      >
        <div className="h-full flex flex-col p-5 bg-white">
          {/* Logo */}
          <div className="text-center mb-4">
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" className="h-12 mx-auto object-contain" />
            ) : (
              <div className="text-2xl font-bold tracking-wide" style={{ color: d.primary_color }}>ADAG PRINT</div>
            )}
          </div>
          
          {/* Headline */}
          <div className="text-center mb-5">
            <h2 className="text-lg font-bold" style={{ color: d.secondary_color }}>{d.headline}</h2>
            {d.subline && <p className="text-sm mt-1" style={{ color: d.primary_color }}>{d.subline}</p>}
          </div>
          
          {/* Divider */}
          <div className="w-16 h-1 mx-auto mb-5 rounded" style={{ backgroundColor: d.primary_color }}></div>
          
          {/* Bullets */}
          <div className="flex-1 flex flex-col justify-center space-y-3 px-3">
            {d.bullets?.map((bullet, i) => (
              <div key={i} className="flex items-center gap-3 text-sm" style={{ color: d.secondary_color }}>
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: d.primary_color }}>
                  <span className="text-white text-xs">✓</span>
                </div>
                <span>{bullet}</span>
              </div>
            ))}
          </div>
          
          {/* Coupon (if set) */}
          {d.coupon_code && (
            <div className="text-center my-4 p-3 rounded-lg border-2 border-dashed" style={{ borderColor: d.primary_color }}>
              <div className="text-xs text-gray-600 mb-1">{d.coupon_text || 'Dein Gutscheincode:'}</div>
              <div className="font-bold text-lg" style={{ color: d.primary_color }}>{d.coupon_code}</div>
            </div>
          )}
          
          {/* Contact Section */}
          <div className="border-t-2 pt-4 mt-auto text-center" style={{ borderColor: d.primary_color + '30' }}>
            <div className="font-bold text-base" style={{ color: d.primary_color }}>{d.website}</div>
            <div className="text-gray-600 text-sm mt-1">{d.phone}</div>
            {d.address && <div className="text-gray-500 text-xs mt-1">{d.address}</div>}
          </div>
          
          {/* QR Code placeholder */}
          <div className="flex justify-center mt-3">
            <div 
              className="w-14 h-14 border-2 rounded flex items-center justify-center text-xs" 
              style={{ borderColor: d.primary_color, color: d.primary_color }}
            >
              QR
            </div>
          </div>
          
          {/* Footer */}
          <div className="text-center text-xs text-gray-400 mt-3 pt-2 border-t border-gray-200">
            Gesponserte Rückseite | Gedruckt von ADAG Print
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="sponsoring-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Partner & Sponsoring</h1>
          <p className="text-gray-600">Sponsoren erhalten Werbeplatz - Kunden erhalten Rabatt</p>
        </div>
        <div className="flex gap-2">
          {sponsors.length === 0 && (
            <button
              onClick={initDefaultSponsor}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              data-testid="init-adag-btn"
            >
              <Star size={20} />
              ADAG Default anlegen
            </button>
          )}
          <button
            onClick={() => { resetForm(); setEditingSponsor(null); setShowDialog(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            data-testid="add-sponsor-btn"
          >
            <Plus size={20} />
            Neuer Sponsor
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Users className="text-green-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-600">Aktive Sponsoren</p>
                <p className="text-2xl font-bold">{stats.active_sponsors}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Inbox className="text-yellow-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-600">Offene Anfragen</p>
                <p className="text-2xl font-bold">{registrations.filter(r => r.status === 'pending').length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <TrendingUp className="text-blue-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-600">Gesponserte Bestellungen</p>
                <p className="text-2xl font-bold">{stats.total_orders}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Percent className="text-purple-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-600">Kundenersparnis</p>
                <p className="text-2xl font-bold">CHF {stats.total_savings?.toFixed(0) || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Download className="text-orange-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-600">Gedruckte Flyer</p>
                <p className="text-2xl font-bold">{sponsors.reduce((sum, s) => sum + (s.usage_count || 0), 0)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex gap-1 mb-4 bg-gray-100 p-1 rounded-lg w-fit">
        <button
          onClick={() => setActiveTab('sponsors')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition ${
            activeTab === 'sponsors' 
              ? 'bg-white shadow text-gray-900' 
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Users size={16} className="inline mr-2" />
          Sponsoren ({sponsors.length})
        </button>
        <button
          onClick={() => setActiveTab('registrations')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition ${
            activeTab === 'registrations' 
              ? 'bg-white shadow text-gray-900' 
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Inbox size={16} className="inline mr-2" />
          Anfragen ({registrations.length})
          {registrations.filter(r => r.status === 'pending').length > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-yellow-500 text-white text-xs rounded-full">
              {registrations.filter(r => r.status === 'pending').length}
            </span>
          )}
        </button>
      </div>

      {/* Registrations Tab */}
      {activeTab === 'registrations' && (
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-4 border-b">
            <h2 className="font-semibold">Sponsor-Anfragen ({registrations.length})</h2>
            <p className="text-sm text-gray-500">Interessenten, die sich über das Formular beworben haben</p>
          </div>
          
          {registrations.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Inbox size={48} className="mx-auto mb-4 text-gray-300" />
              <p>Noch keine Anfragen vorhanden.</p>
              <p className="text-sm mt-2">Anfragen kommen über <a href="https://adagmedia.ch/flyer-sponsor-werden" target="_blank" rel="noopener noreferrer" className="text-green-600 underline">adagmedia.ch/flyer-sponsor-werden</a></p>
            </div>
          ) : (
            <div className="divide-y">
              {registrations.map(reg => (
                <div key={reg.id} className={`p-4 ${reg.status === 'approved' ? 'bg-green-50/50' : reg.status === 'rejected' ? 'bg-red-50/50' : ''}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center text-white font-bold">
                          {reg.company?.charAt(0) || 'S'}
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{reg.company}</h3>
                          <p className="text-sm text-gray-500">{reg.industry}</p>
                        </div>
                        {getStatusBadge(reg.status)}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mt-3 text-sm">
                        <div className="flex items-center gap-2 text-gray-600">
                          <Users size={14} className="text-gray-400" />
                          <span>{reg.contact}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Mail size={14} className="text-gray-400" />
                          <a href={`mailto:${reg.email}`} className="text-green-600 hover:underline">{reg.email}</a>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Phone size={14} className="text-gray-400" />
                          <a href={`tel:${reg.phone}`} className="hover:underline">{reg.phone}</a>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <MapPin size={14} className="text-gray-400" />
                          <span>{reg.location}</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4 mt-3 text-sm">
                        <div className="flex items-center gap-2">
                          <Building2 size={14} className="text-gray-400" />
                          <span className="text-gray-600">Budget: <strong className="text-green-600">CHF {reg.budget}/Mt.</strong></span>
                        </div>
                        <div className="text-gray-400">|</div>
                        <div className="text-gray-500">
                          Eingereicht: {formatDate(reg.created_at)}
                        </div>
                      </div>
                      
                      {reg.message && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
                          <strong className="text-gray-700">Nachricht:</strong> {reg.message}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex flex-col gap-2">
                      {reg.status === 'pending' && (
                        <>
                          <button
                            onClick={() => approveRegistration(reg.id)}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                            data-testid={`approve-${reg.id}`}
                          >
                            <CheckCircle size={16} />
                            Freischalten
                          </button>
                          <button
                            onClick={() => {/* TODO: reject */}}
                            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 text-sm"
                          >
                            <XCircle size={16} />
                            Ablehnen
                          </button>
                        </>
                      )}
                      {reg.status === 'approved' && (
                        <span className="text-green-600 text-sm">Als Sponsor aktiv</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Sponsors List */}
      {activeTab === 'sponsors' && (
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="font-semibold">Alle Sponsoren ({sponsors.length})</h2>
        </div>
        
        {sponsors.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p>Noch keine Sponsoren vorhanden.</p>
            <p className="text-sm mt-2">Klicke "ADAG Default anlegen" um den Standard-Sponsor zu erstellen.</p>
          </div>
        ) : (
          <div className="divide-y">
            {sponsors.map(sponsor => (
              <div key={sponsor.id} className={`p-4 flex items-center justify-between ${!sponsor.is_active ? 'bg-gray-50 opacity-60' : ''}`}>
                <div className="flex items-center gap-4">
                  {sponsor.logo_url ? (
                    <img src={sponsor.logo_url} alt={sponsor.name} className="w-12 h-12 object-contain rounded" />
                  ) : (
                    <div className="w-12 h-12 bg-green-100 rounded flex items-center justify-center text-green-600 font-bold">
                      {sponsor.company?.charAt(0) || 'S'}
                    </div>
                  )}
                  <div>
                    <h3 className="font-medium flex items-center gap-2">
                      {sponsor.name}
                      {sponsor.is_default && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Standard</span>
                      )}
                    </h3>
                    <p className="text-sm text-gray-600">{sponsor.company}</p>
                    <div className="flex gap-2 mt-1 flex-wrap">
                      {sponsor.categories?.map(cat => (
                        <span key={cat} className={`text-xs px-2 py-0.5 rounded ${getCategoryStyle(cat)}`}>
                          {getCategoryLabel(cat)}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">-{sponsor.discount_percent}%</p>
                    <p className="text-xs text-gray-500">Rabatt</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-semibold">{sponsor.usage_count || 0}</p>
                    <p className="text-xs text-gray-500">Drucke</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => { 
                        setFormData(prev => ({
                          ...prev, 
                          design: sponsor.design || DEFAULT_DESIGN,
                          logo_url: sponsor.logo_url || ''
                        })); 
                        setShowPreview(true); 
                      }}
                      className="p-2 text-gray-600 hover:bg-gray-50 rounded"
                      title="Vorschau"
                    >
                      <Eye size={18} />
                    </button>
                    <button
                      onClick={() => toggleActive(sponsor)}
                      className={`p-2 rounded ${sponsor.is_active ? 'text-green-600' : 'text-gray-400'}`}
                      title={sponsor.is_active ? 'Aktiv' : 'Inaktiv'}
                    >
                      {sponsor.is_active ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                    </button>
                    <button
                      onClick={() => openEdit(sponsor)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                    >
                      <Edit2 size={18} />
                    </button>
                    {!sponsor.is_default && (
                      <button
                        onClick={() => handleDelete(sponsor.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Rückseiten-Vorschau (A5)</h2>
              <button onClick={() => setShowPreview(false)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            <div className="flex justify-center">
              <SponsorPreview design={formData.design} logoUrl={formData.logo_url} />
            </div>
            <p className="text-center text-sm text-gray-500 mt-4">
              So erscheint die gesponserte Rückseite auf dem Flyer
            </p>
          </div>
        </div>
      )}

      {/* Add/Edit Dialog */}
      {showDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 overflow-y-auto py-8">
          <div className="bg-white rounded-lg w-full max-w-4xl m-4">
            <div className="p-4 border-b sticky top-0 bg-white z-10">
              <h2 className="text-lg font-semibold">
                {editingSponsor ? 'Sponsor bearbeiten' : 'Neuer Sponsor'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column: Basic Info */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-700 border-b pb-2">Grunddaten</h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Name *</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={e => setFormData({...formData, name: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder="z.B. ADAG Eigenwerbung"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Firma *</label>
                      <input
                        type="text"
                        required
                        value={formData.company}
                        onChange={e => setFormData({...formData, company: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder="z.B. ADAG Print"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Logo</label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-green-500 transition-colors">
                      {formData.logo_url ? (
                        <div className="relative inline-block">
                          <img 
                            src={formData.logo_url} 
                            alt="Logo" 
                            className="h-20 object-contain rounded"
                            onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23ccc"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>'; }}
                          />
                          <button
                            type="button"
                            onClick={removeLogo}
                            className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <div>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                            onChange={handleLogoUpload}
                            className="hidden"
                            id="logo-upload"
                          />
                          <label 
                            htmlFor="logo-upload" 
                            className="cursor-pointer flex flex-col items-center gap-2"
                          >
                            {uploading ? (
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                            ) : (
                              <>
                                <Upload className="w-8 h-8 text-gray-400" />
                                <span className="text-sm text-gray-600">Logo hochladen</span>
                                <span className="text-xs text-gray-400">PNG, JPG, WEBP oder SVG (max. 2MB)</span>
                              </>
                            )}
                          </label>
                        </div>
                      )}
                    </div>
                    {/* Optional: URL input as fallback */}
                    <div className="mt-2">
                      <details className="text-xs text-gray-500">
                        <summary className="cursor-pointer hover:text-gray-700">Oder URL eingeben</summary>
                        <input
                          type="url"
                          value={formData.logo_url}
                          onChange={e => setFormData({...formData, logo_url: e.target.value})}
                          className="w-full px-2 py-1 border rounded mt-1 text-sm"
                          placeholder="https://..."
                        />
                      </details>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Rabatt (%)</label>
                      <input
                        type="number"
                        min="1"
                        max="50"
                        value={formData.discount_percent}
                        onChange={e => setFormData({...formData, discount_percent: parseInt(e.target.value)})}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Priorität</label>
                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={formData.priority}
                        onChange={e => setFormData({...formData, priority: parseInt(e.target.value)})}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                      <p className="text-xs text-gray-500 mt-1">1 = höchste Priorität</p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Kategorie</label>
                    <div className="flex flex-wrap gap-2">
                      {CATEGORIES.map(cat => (
                        <button
                          key={cat.id}
                          type="button"
                          onClick={() => {
                            const cats = formData.categories.includes(cat.id)
                              ? formData.categories.filter(c => c !== cat.id)
                              : [...formData.categories, cat.id];
                            setFormData({...formData, categories: cats});
                          }}
                          className={`px-3 py-1 rounded-full text-sm transition ${
                            formData.categories.includes(cat.id)
                              ? cat.color + ' ring-2 ring-offset-1'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {cat.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.is_default}
                        onChange={e => setFormData({...formData, is_default: e.target.checked})}
                        className="rounded"
                      />
                      <span className="text-sm">Standard-Sponsor (Fallback)</span>
                    </label>
                  </div>
                </div>

                {/* Right Column: Design */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-700 border-b pb-2">Rückseiten-Design</h3>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Headline *</label>
                    <input
                      type="text"
                      required
                      value={formData.design.headline}
                      onChange={e => updateDesign('headline', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Subline</label>
                    <input
                      type="text"
                      value={formData.design.subline || ''}
                      onChange={e => updateDesign('subline', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Bullet Points (3 Stück)</label>
                    {[0, 1, 2].map(i => (
                      <input
                        key={i}
                        type="text"
                        value={formData.design.bullets?.[i] || ''}
                        onChange={e => updateBullet(i, e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg mb-2"
                        placeholder={`Bullet ${i + 1}`}
                      />
                    ))}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Website</label>
                      <input
                        type="text"
                        value={formData.design.website}
                        onChange={e => updateDesign('website', e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Telefon</label>
                      <input
                        type="text"
                        value={formData.design.phone}
                        onChange={e => updateDesign('phone', e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Adresse</label>
                    <input
                      type="text"
                      value={formData.design.address || ''}
                      onChange={e => updateDesign('address', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Gutscheincode (optional)</label>
                      <input
                        type="text"
                        value={formData.design.coupon_code || ''}
                        onChange={e => updateDesign('coupon_code', e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder="z.B. FLYER10"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Gutschein-Text</label>
                      <input
                        type="text"
                        value={formData.design.coupon_text || ''}
                        onChange={e => updateDesign('coupon_text', e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder="z.B. 10% auf nächste Bestellung"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Primärfarbe</label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={formData.design.primary_color}
                          onChange={e => updateDesign('primary_color', e.target.value)}
                          className="w-12 h-10 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={formData.design.primary_color}
                          onChange={e => updateDesign('primary_color', e.target.value)}
                          className="flex-1 px-3 py-2 border rounded-lg"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Sekundärfarbe</label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={formData.design.secondary_color}
                          onChange={e => updateDesign('secondary_color', e.target.value)}
                          className="w-12 h-10 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={formData.design.secondary_color}
                          onChange={e => updateDesign('secondary_color', e.target.value)}
                          className="flex-1 px-3 py-2 border rounded-lg"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Mini Preview */}
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <div className="text-sm font-medium mb-2 text-gray-600">Vorschau</div>
                    <div className="transform scale-50 origin-top-left" style={{ width: '200%', height: '200px' }}>
                      <SponsorPreview design={formData.design} logoUrl={formData.logo_url} size="a5" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t mt-6">
                <button
                  type="button"
                  onClick={() => setShowPreview(true)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50 flex items-center gap-2"
                >
                  <Eye size={18} />
                  Vollansicht
                </button>
                <button
                  type="button"
                  onClick={() => { setShowDialog(false); setEditingSponsor(null); }}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  {editingSponsor ? 'Speichern' : 'Erstellen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
